/**
 * Import an opinion dataset: from a Voxit report already indexed into the knowledge
 * base, or from a Pol.is/Voxit CSV export (specs/opinion-datasets.md, #1179).
 * The same identifier updates an existing dataset; its group names and profiles stay.
 */
import React, { useEffect, useState } from 'react';
import { Input, Select, Label, Button, Actions, StatusLine, FieldHelp } from '../../../components/InstanceSettings/formPrimitives';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { importOpinionDataset, searchSourceDocuments } from './opinionApi';
import { Section, SectionTitle, FieldRow, Table, ErrorText } from '../DatasetWorkspace/styles';

const SLUG = /^[a-z0-9][a-z0-9-]{1,99}$/;

const readFile = (file) => (file ? file.text() : Promise.resolve(null));

export default function ImportOpinionForm({ onDone, onCancel }) {
  const { t } = useTranslation();
  const [format, setFormat] = useState('voxit_pdf');
  const [datasetId, setDatasetId] = useState('');
  const [name, setName] = useState('');
  const [query, setQuery] = useState('voxit');
  const [documents, setDocuments] = useState([]);
  const [documentId, setDocumentId] = useState(null);
  const [groupsFile, setGroupsFile] = useState(null);
  const [commentsFile, setCommentsFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (format !== 'voxit_pdf') return undefined;
    let live = true;
    const timer = setTimeout(() => {
      searchSourceDocuments(query.trim())
        .then((docs) => { if (live) setDocuments(docs); })
        .catch((err) => { if (live) setError(err.message); });
    }, 250);
    return () => { live = false; clearTimeout(timer); };
  }, [format, query]);

  const ready = SLUG.test(datasetId) && (format === 'voxit_pdf' ? Boolean(documentId) : Boolean(name.trim() && groupsFile));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const payload = format === 'voxit_pdf'
        ? { format, datasetId, name: name.trim() || undefined, documentId }
        : {
          format,
          datasetId,
          name: name.trim(),
          files: { commentGroups: await readFile(groupsFile), comments: await readFile(commentsFile) },
        };
      const result = await importOpinionDataset(payload);
      setDone(result);
      onDone(result.datasetId);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section>
      <SectionTitle>{t('opinionDatasets.import.title')}</SectionTitle>
      <form onSubmit={submit}>
        <FieldRow>
          <div>
            <Label htmlFor="opinion-format">{t('opinionDatasets.import.format')}</Label>
            <Select id="opinion-format" value={format} onChange={(e) => setFormat(e.target.value)}>
              <option value="voxit_pdf">{t('opinionDatasets.import.voxitPdf')}</option>
              <option value="polis_csv">{t('opinionDatasets.import.polisCsv')}</option>
            </Select>
          </div>
          <div>
            <Label htmlFor="opinion-id">{t('opinionDatasets.import.datasetId')}</Label>
            <Input id="opinion-id" value={datasetId} onChange={(e) => setDatasetId(e.target.value.toLowerCase())} />
            <FieldHelp>{t('opinionDatasets.import.datasetIdHelp')}</FieldHelp>
          </div>
          <div>
            <Label htmlFor="opinion-name">{t('opinionDatasets.import.name')}</Label>
            <Input id="opinion-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
        </FieldRow>

        {format === 'voxit_pdf' ? (
          <>
            <Label htmlFor="opinion-doc-search">{t('opinionDatasets.import.documentSearch')}</Label>
            <Input id="opinion-doc-search" value={query} onChange={(e) => setQuery(e.target.value)} />
            {documents.length === 0 ? (
              <FieldHelp>{t('opinionDatasets.import.documentNone')}</FieldHelp>
            ) : (
              <Table>
                <tbody>
                  {documents.map((d) => (
                    <tr key={d.id} data-clickable onClick={() => setDocumentId(d.id)}>
                      <td>
                        <input type="radio" name="opinion-doc" checked={documentId === d.id} onChange={() => setDocumentId(d.id)} />
                      </td>
                      <td>{d.title || d.filename}</td>
                      <td>{t('opinionDatasets.import.documentChunks', { chunks: d.chunks })}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        ) : (
          <FieldRow>
            <div>
              <Label htmlFor="opinion-groups-file">{t('opinionDatasets.import.commentGroups')}</Label>
              <Input id="opinion-groups-file" type="file" accept=".csv,text/csv" onChange={(e) => setGroupsFile(e.target.files?.[0] || null)} />
            </div>
            <div>
              <Label htmlFor="opinion-comments-file">{t('opinionDatasets.import.comments')}</Label>
              <Input id="opinion-comments-file" type="file" accept=".csv,text/csv" onChange={(e) => setCommentsFile(e.target.files?.[0] || null)} />
            </div>
          </FieldRow>
        )}

        <Actions>
          <Button type="submit" $variant="primary" disabled={!ready || busy}>{t('opinionDatasets.import.submit')}</Button>
          <Button type="button" $variant="ghost" onClick={onCancel}>{t('opinionDatasets.import.cancel')}</Button>
          {busy && <StatusLine>{t('opinionDatasets.import.importing')}</StatusLine>}
          {done && !busy && (
            <StatusLine>
              {t('opinionDatasets.import.done', { opinions: done.opinions, background: done.background, groups: done.groups })}
            </StatusLine>
          )}
        </Actions>
        {error && <ErrorText>{error}</ErrorText>}
      </form>
    </Section>
  );
}
